import type {
  CompanyAnalytics,
  CompanyAnalyticsCategory,
  CompanyAnalyticsContract,
} from "@/modules/companies/client/api/types"

import { isAfter, isBefore, subMonths } from "date-fns"

const MINIMUM_OFFERS_PUBLISHED = 20
export const PUBLICATION_MONTH_THRESHOLD = 12

export const getStartMonth = (): Date => {
  const dateNow = new Date()
  return new Date(Date.UTC(dateNow.getUTCFullYear(), dateNow.getUTCMonth()))
}

type Entry = {
  dateMonth: string
}

export const getFilteredByThreshold = <P extends Entry>(data: P[]): P[] => {
  return data.filter(entry => {
    return (
      isBefore(new Date(entry.dateMonth), getStartMonth()) &&
      isAfter(new Date(entry.dateMonth), subMonths(getStartMonth(), PUBLICATION_MONTH_THRESHOLD))
    )
  })
}

export const getScale = (value: number): number => {
  let scale = 25

  if (value > scale) {
    const dozen = Math.floor((value / 10) % 10)
    const additionalValue = dozen < 5 ? 50 : 100
    scale = Math.floor(value / 100) * 100 + additionalValue
  }

  return scale
}

type GetFilteredByContract = {
  name: string
  publicationsCount: number
}

export const getFilteredByContract = (data: CompanyAnalyticsContract[]): GetFilteredByContract[] => {
  return (
    data
      // reduce entries to have overall publications by month
      .reduce((accumulator, currentValue) => {
        // find index if month already exists
        const indexContract = accumulator.findIndex(computedContract => {
          return computedContract.name === currentValue.contractType
        })
        if (indexContract >= 0) {
          // increment counter
          accumulator[indexContract].publicationsCount += currentValue.publicationsCount
        } else {
          // add new entry
          accumulator.push({
            name: currentValue.contractType,
            publicationsCount: currentValue.publicationsCount,
          })
        }
        return accumulator
      }, [] as GetFilteredByContract[])
      // sort values by publication (and alphabetical order in case of equality)
      .sort((contractA, contractB) => {
        if (contractA.publicationsCount < contractB.publicationsCount) {
          return 1
        }
        if (contractA.publicationsCount > contractB.publicationsCount) {
          return -1
        }
        // equality case
        return contractA.name.localeCompare(contractB.name)
      })
  )
}

type GetHasEnoughData = CompanyAnalytics | undefined

export const getHasEnoughData = (dataCompanyAnalytics: GetHasEnoughData): boolean => {
  if (!dataCompanyAnalytics) return false

  // NB: positionCategories is used, but it could have been other objects
  const totalPublication = getFilteredByThreshold<CompanyAnalyticsCategory>(dataCompanyAnalytics.positionCategories)
    // get total publication amount
    .reduce((accumulator, currentValue) => {
      return accumulator + currentValue.publicationsCount
    }, 0)

  return totalPublication >= MINIMUM_OFFERS_PUBLISHED
}

type FilteredPosition = {
  positionCategoryName: string
  publicationsCount: number
}

export type TopPositions = {
  filteredPosition: FilteredPosition[]
  scale: number
} | null

export const getTopPositions = (dataCompanyAnalytics: CompanyAnalytics): TopPositions => {
  const filteredPosition = getFilteredByThreshold<CompanyAnalyticsCategory>(dataCompanyAnalytics.positionCategories)
    // reduce entries to have overall publications by positions
    .reduce((accumulator, currentValue) => {
      // search if there is already an entry with positionCategoryName in the accumulator
      const indexComputedPosition = accumulator.findIndex(computedPosition => {
        return computedPosition.positionCategoryName === currentValue.positionCategoryName
      })
      // if so, increment publicationsCount
      if (indexComputedPosition >= 0) {
        accumulator[indexComputedPosition].publicationsCount += currentValue.publicationsCount
      }
      // otherwise, add new entry
      else {
        accumulator.push({
          positionCategoryName: currentValue.positionCategoryName,
          publicationsCount: currentValue.publicationsCount,
        })
      }
      return accumulator
    }, [] as FilteredPosition[])
    // sort values by publication (and alphabetical order in cas of equality)
    .sort((positionA, positionB) => {
      if (positionA.publicationsCount < positionB.publicationsCount) {
        return 1
      }
      if (positionA.publicationsCount > positionB.publicationsCount) {
        return -1
      }
      // equality case
      return positionA.positionCategoryName.localeCompare(positionB.positionCategoryName)
    })
    // take 4 first entries
    .slice(0, 4)

  return {
    filteredPosition,
    scale: getScale(filteredPosition[0].publicationsCount),
  }
}

export type Contract = {
  name: string
  percent: number
  displayPercent: number
}
export type TopContracts = {
  contracts: Contract[]
  totalPositions: number
} | null

export const strip0percent = (contracts: Contract[]): Contract[] => {
  // remove 0% contract, not meaningful to display
  const index = contracts.findIndex(contract => {
    return contract.displayPercent <= 0
  })

  if (index === -1) return contracts
  return contracts.slice(0, index)
}

export const roundPercentContracts = (contracts: Contract[]): Contract[] => {
  // percent need to be displayed as integers, they need to be rounded.
  // but rounding may lead to percent not equal to 100 %, then the difference is distributed between displayed percent
  // (see unit test for examples)
  let globalDiff = 100
  const rest: { index: number; decimal: number }[] = []
  contracts.forEach((contract, index) => {
    globalDiff -= contract.displayPercent
    rest.push({ decimal: contract.percent % 1, index })
  })

  // do nothing if globalDiff === 0, meaning that addition of all displayed percent is equal to 100
  if (globalDiff !== 0) {
    if (globalDiff < 0) {
      // Addition of all displayed percent is superior to 100, then sort the rest array descending
      rest.sort((entryA, entryB) => {
        return entryA.decimal - entryB.decimal
      })
      // decrease some displayed percent to have a total of 100 displayed
      for (let pointer = 0; pointer < Math.abs(globalDiff); pointer++) {
        // eslint-disable-next-line no-param-reassign
        contracts[rest[pointer].index].displayPercent -= 1
      }
    } else {
      // Addition of all displayed percent is superior to 100, then sort the rest array ascending
      rest.sort((entryA, entryB) => {
        return entryB.decimal - entryA.decimal
      })
      // increase some displayed percent to have a total of 100 displayed
      for (let increment = 0; increment < globalDiff; increment++) {
        // eslint-disable-next-line no-param-reassign
        contracts[rest[increment].index].displayPercent += 1
      }
    }
  }

  return strip0percent(contracts)
}

export const getTopContracts = (dataCompanyAnalytics: CompanyAnalytics): TopContracts => {
  const filteredContracts = getFilteredByThreshold<CompanyAnalyticsContract>(dataCompanyAnalytics.contractTypes)
  const totalPositions = filteredContracts.reduce((accumulator, currentValue) => {
    return accumulator + currentValue.publicationsCount
  }, 0)

  const overallContract = getFilteredByContract(filteredContracts)

  // reduce entries to have overall percent by contract (for 3 first entries)
  const slicedContracts = overallContract.slice(0, 3).reduce((accumulator, currentValue) => {
    const percent = (currentValue.publicationsCount / totalPositions) * 100
    accumulator.push({
      displayPercent: Math.round(percent),
      name: currentValue.name,
      percent,
    })
    return accumulator
  }, [] as Contract[])

  // regroup rest in a global "other" group
  const otherContractsCount = overallContract.slice(3).reduce((accumulator, currentValue) => {
    return accumulator + currentValue.publicationsCount
  }, 0)
  const otherContractPercent = (otherContractsCount / totalPositions) * 100

  const contracts = [
    ...slicedContracts,
    {
      displayPercent: Math.round(otherContractPercent),
      name: "Other",
      percent: otherContractPercent,
    },
  ]

  return {
    contracts: roundPercentContracts(contracts),
    totalPositions,
  }
}

export type ContractCount = Record<string, number>

type Month = {
  month: string
  totalPublicationsCount: number
  contractCount: ContractCount
}

export type TopMonths = {
  months: Month[]
  scale: number
} | null

export const getThreeTopMonths = (dataCompanyAnalytics: CompanyAnalytics): TopMonths => {
  const filteredContracts = getFilteredByThreshold<CompanyAnalyticsContract>(dataCompanyAnalytics.contractTypes)

  const topContractsName = getFilteredByContract(filteredContracts)
    .slice(0, 3)
    // reduce entries to have only contract name
    .reduce((accumulator, currentValue) => {
      return [...accumulator, currentValue.name]
    }, [] as string[])

  const topMonths = filteredContracts
    // reduce entries to have month with the 3 more publications of the top contract names
    .reduce((accumulator, currentValue) => {
      // if the contract type is not one of the top contract names, go to the next entry
      if (!topContractsName.includes(currentValue.contractType)) return accumulator

      // find index if month already exists
      const indexComputedMonth = accumulator.findIndex(computedMonth => {
        return computedMonth.month === currentValue.dateMonth
      })

      if (indexComputedMonth >= 0) {
        // update already available entry
        accumulator[indexComputedMonth].totalPublicationsCount += currentValue.publicationsCount
        accumulator[indexComputedMonth].contractCount[currentValue.contractType] += currentValue.publicationsCount
      } else {
        // create a new one
        const obj: Month = {
          contractCount: {},
          month: currentValue.dateMonth,
          totalPublicationsCount: currentValue.publicationsCount,
        }
        topContractsName.forEach(name => {
          obj.contractCount[name] = name === currentValue.contractType ? currentValue.publicationsCount : 0
        })
        accumulator.push(obj)
      }

      return accumulator
    }, [] as Month[])
    .filter(month => month.totalPublicationsCount > 0)
    .sort((contractA, contractB) => {
      return contractB.totalPublicationsCount - contractA.totalPublicationsCount
    })
    .slice(0, 3)

  return {
    months: topMonths,
    scale: getScale(topMonths[0].totalPublicationsCount),
  }
}
